import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  useCallback,
} from 'react';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import {
  initItemsApp,
  resetItemsApp,
} from '../../scripts/learnosityScripts.js';
import { AssessmentContext } from '../../context/AssessmentContext.js';
import { Observable } from '../../scripts/common/Observable.js';
import { useExternalScript } from '../../hooks/useExternalScript.js';
import { LEARNOSITY_LAUNCH_FAIL } from '../../context/PwaEvents.js';

//wait is true, so notify runs synchronously.
//priority on subscribe will call the methods in priority order
//otherwise it will get invoked in the order of subscription
export const LearnosityContainerObservable = new Observable(
  'learnosityEvents',
  true
);

const LearnosityContainer = () => {
  const learnosityScriptUrl = `${window.env.REACT_APP_LEARNOSITY_ITEMS_API_PREFIX}?${window.env.REACT_APP_LTS_VERSION}`;
  const isLearnosityScriptAvailable = () => {
    return window.LearnosityItems;
  };
  const learnosityState = useExternalScript(
    learnosityScriptUrl,
    isLearnosityScriptAvailable
  );
  const [open, setOpen] = useState(true);

  const { learnosityRequestObj, setLearnosityItemApp, pnp } =
    useContext(AssessmentContext);

  const ttsScriptUrl = window.env.REACT_APP_TEXTTOSPEECH_URL;
  const isTTSScriptAvailable = () => {
    return window.speechstream;
  };
  useExternalScript(ttsScriptUrl, isTTSScriptAvailable, {
    appendHead: false,
    scriptAttributes: {
      'data-speechstream-config': window.env.REACT_APP_TEXTTOSPEECH_CONFIG,
    },
    ignore: !pnp?.spoken?.activateByDefault,
  });

  const learnosityComponentLoading = useRef(false);
  const setLearnosityComponentLoading = (val) => {
    learnosityComponentLoading.current = val;
  };

  //useRef to hold on to the ready state returned by learnosity API
  //for error cases not to show launch error
  //make it a ref instead of state to avoid reloads
  const learnosityComponentReady = useRef(false);
  const setLearnosityComponentReady = (val) => {
    learnosityComponentReady.current = val;
  };

  const emitEvent = useCallback((event) => {
    LearnosityContainerObservable.notify({
      ...event,
      //pass the ready state in ref
      ready: learnosityComponentReady.current,
    });
  }, []);

  const learnosityComponentCallback = useCallback(
    ({ itemsApp, ready: appReady, error }) => {
      setOpen(false);
      if (appReady) {
        //Set items App only if the learnosity container is ready
        //appReady is set to true only if called from ready listener
        setLearnosityItemApp(itemsApp);
        setLearnosityComponentReady(appReady);
      }
      if (error) {
        emitEvent({
          eventName: LEARNOSITY_LAUNCH_FAIL,
          error: error,
        });
      }
    },
    [setLearnosityItemApp, emitEvent]
  );

  useEffect(() => {
    if (learnosityState !== 'ready') {
      return;
    }
    var itemsApp = null;
    const callLearnosityAPI = async () => {
      console.log('Loading Learnosity section...');
      setOpen(true);
      try {
        if (!learnosityComponentLoading.current) {
          resetItemsApp(itemsApp);
          console.log('Calling initItemsApp...');
          itemsApp = initItemsApp(
            learnosityRequestObj,
            setLearnosityComponentLoading,
            learnosityComponentCallback,
            emitEvent,
            pnp
          );
        }
      } catch (error) {
        console.log(
          'Error occurred while loading learnosity component. ',
          error
        );
        setOpen(false);
        setLearnosityComponentLoading(false);
        resetItemsApp(itemsApp);
        setLearnosityItemApp(null);
        emitEvent({
          eventName: LEARNOSITY_LAUNCH_FAIL,
          error: error,
        });
      }
    };
    callLearnosityAPI().catch(console.error);

    //unmounting cleanup
    return () => {
      console.log('unmount');
      setOpen(false);
      //only reset if learnosity component is not currently loading, meaning init is done/ready
      if (!learnosityComponentLoading.current) {
        resetItemsApp(itemsApp);
        setLearnosityItemApp(null);
      }
    };
  }, [
    emitEvent,
    learnosityRequestObj,
    pnp,
    learnosityComponentCallback,
    learnosityState,
    setLearnosityItemApp,
  ]);

  return (
    <React.Fragment>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={open}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div>
        {/* SpeechStream start point indicator */}
        <span id="start"></span>
        <div id="learnosity_assess" style={{ minHeight: '85vh' }}></div>
      </div>
    </React.Fragment>
  );
};

export default LearnosityContainer;
